import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getCurrentDate } from "../utils/getCurrentDate";
import { useNavigate } from "react-router-dom";
import {
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
} from "../slices/OrdersSlice";
import { toast } from "react-toastify";
import ReUsableModal from "../components/ReUsableModal";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Loader from "../components/Loader";
import Message from "../components/Message";

const OrdersHistoryScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { newOrders } = useSelector((state) => state.newOrders);
  const [orderId, setOrderId] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [deliveryOtp, setDeliveryOtp] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [orderQuantity, setOrderQuantity] = useState([]);
  const [displayOrderId, setDisplayOrderId] = useState("");
  const [apiQuery, setApiQuery] = useState(
    `createdAt[gte]=${getCurrentDate()}&sort=-createdAt`
  );
  const [showDelivered, setShowDelivered] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const navigate = useNavigate();
  const [showOutletOrders, setShowOutletOrders] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [hasClickedFilter, setHasClickedFilter] = useState(false);

  const {
    data: orders,
    isLoading,
    error,
    refetch: orderRefetch,
  } = useGetOrdersQuery({ query: apiQuery, token: userInfo?.token });

  const [ordersToList, setOrdersToList] = useState(orders);

  const [updateStatus, { isLoading: updateStatusLoading }] =
    useUpdateOrderStatusMutation();

  useEffect(() => {
    if (!ordersToList) return;
    if (!hasClickedFilter && !searchText.trim()) {
      setFilteredOrders(ordersToList);
      return;
    }

    const term = searchText.trim().toLowerCase();

    let updatedOrders = [];

    if (term) {
      if (term.length < 4) {
        // Match only orderId
        updatedOrders = ordersToList.filter((order) => {
          const orderIdStr = String(order?.orderId || "").toLowerCase();
          return orderIdStr.includes(term);
        });
      } else {
        // Match only mobileNumber
        updatedOrders = ordersToList.filter((order) => {
          const mobileNumberStr = String(
            order?.mobileNumber || ""
          ).toLowerCase();
          return mobileNumberStr.includes(term);
        });
      }
    } else {
      // If no search term, use filters
      if (showOutletOrders) {
        updatedOrders = ordersToList.filter(
          (order) => order.orderType === "outlet"
        );
      } else if (showDelivered) {
        updatedOrders = ordersToList.filter(
          (order) =>
            order.orderStatus === "Delivered" && order.orderType === "online"
        );
      } else {
        updatedOrders = ordersToList.filter(
          (order) =>
            order.orderStatus !== "Delivered" && order.orderType === "online"
        );
      }
    }

    setFilteredOrders(updatedOrders);
  }, [
    searchText,
    ordersToList,
    showDelivered,
    showOutletOrders,
    hasClickedFilter,
  ]);

  const handleOrderStatusUpdate = async () => {
    try {
      const data = {
        _id: orderId,
        orderStatus: newStatus,
        deliveryOtp: deliveryOtp,
        token: userInfo.token,
      };
      await updateStatus(data).unwrap();
      toast.success("Status Updated Successfully");
      orderRefetch();
    } catch (e) {
      toast.error(e?.data?.error);
    }
  };

  const handleShowDelivered = () => {
    if (!ordersToList) return;
    const today = new Date();
    const deliveredToday = ordersToList?.filter(
      (order) => order?.orderStatus === "Delivered"
    );
    setFilteredOrders(deliveredToday);
    setShowDelivered(true);
    setShowOutletOrders(false);
    setHasClickedFilter(true);
  };

  useEffect(() => {
    let orderDetails = {};
    const ors = isLoading ? [] : orders ? orders : [];
    setOrdersToList(ors);
    ors?.map((allOrs) => {
      if (allOrs.orderStatus !== "Delivered") {
        allOrs?.orderDetails?.map((or) => {
          if (orderDetails[or.foodItemId + "_" + or.itemName]) {
            orderDetails[or.foodItemId + "_" + or.itemName] += or.itemQuantity;
          } else {
            orderDetails[or.foodItemId + "_" + or.itemName] = or.itemQuantity;
          }
        });
      }
    });
    setOrderQuantity(orderDetails);
  }, [orders]);

  useEffect(() => {
    const ors = isLoading ? [] : orders || [];
    setOrdersToList(ors);
    if (!hasClickedFilter) {
      setFilteredOrders(ors); // show all orders on load
    }
  }, [orders]);

  const handleShowAllOrders = () => {
    if (!orders) return;
    const nonDelivered = orders.filter(
      (order) =>
        order.orderStatus !== "Delivered" && order.orderType === "online"
    );
    setFilteredOrders(nonDelivered);
    setShowDelivered(false);
    setShowOutletOrders(false);
    setHasClickedFilter(true);
  };

  const handleShowOutletOrders = () => {
    setShowDelivered(false);
    setShowOutletOrders(true);
    setHasClickedFilter(true);
  };

  const getOrderHandler = () => {
    if (endDate < startDate) {
      toast.error("End date cannot be earlier than Start date");
    } else {
      const start = `${startDate}T00:00:00.000Z`;
      const end = `${endDate}T23:59:59.999Z`;
      setApiQuery(
        `createdAt[gte]=${start}&createdAt[lte]=${end}&sort=-createdAt`
      );
      setHasClickedFilter(false);
    }
  };

  return (
    <>
      <h5>Order Details</h5>
      <ReUsableModal
        title="Update Order Status"
        payload={[
          {
            fieldName: "Order ID",
            fieldType: "text",
            fieldValue: displayOrderId,
            setMethod: setDisplayOrderId,
            disabled: true,
          },
          {
            fieldName: "Status",
            fieldType: "select",
            fieldValue: newStatus,
            setMethod: setNewStatus,
            options: [
              "Placed",
              "Confirmed",
              "Preparing",
              "Ready",
              "In_Delivery",
              "Delivered",
              "Delivery_Rejected",
              "Could_Not_Deliver",
              "Not_Placed",
              "Rejected",
            ],
          },
          ...(newStatus === "Delivered"
            ? [
                {
                  fieldName: "Delivery OTP",
                  fieldType: "text",
                  fieldValue: deliveryOtp,
                  setMethod: setDeliveryOtp,
                },
              ]
            : []),
        ]}
        show={showModal}
        submitDisabled={
          newStatus === "Delivered" && !/^\d{4}$/.test(deliveryOtp)
        }
        size="md"
        onHide={() => setShowModal(false)}
        onSubmit={handleOrderStatusUpdate}
        submit="Update"
        cancel="Cancel"
      />

      <br />

      <Row
        style={{
          justifyContent: "left",
          alignItems: "center",
          margin: "auto",
          width: "95%",
        }}
      >
        {/* <Button
          className="marginPointEight sixteenWidth buttonGreen numbersButton"
          onClick={handleShowAllOrders}
        >
          Total: <strong>{ordersToList?.length}</strong>
        </Button> */}
        <Button
          className="marginPointEight sixteenWidth buttonGreen numbersButton"
          onClick={handleShowAllOrders}
        >
          Open Orders:{" "}
          <strong>
            {
              ordersToList?.filter(
                (order) =>
                  order.orderStatus !== "Delivered" &&
                  order.orderType === "online"
              )?.length
            }
          </strong>
        </Button>
        <Button
          className="marginPointEight sixteenWidth buttonGreen numbersButton"
          onClick={handleShowDelivered}
        >
          Delivered Orders:{" "}
          <strong>
            {
              ordersToList?.filter((order) => order.orderStatus === "Delivered")
                ?.length
            }
          </strong>
        </Button>
        <Button
          className="marginPointEight sixteenWidth buttonGreen numbersButton"
          onClick={handleShowOutletOrders}
        >
          Outlet Orders:{" "}
          <strong>
            {
              ordersToList?.filter((order) => order.orderType === "outlet")
                ?.length
            }
          </strong>
        </Button>
        <Form.Control
          type="text"
          placeholder="Order ID or Mobile Number"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value.replace(/\D/g, ""))}
          style={{
            maxWidth: "20rem",
            margin: "0.4rem 0 0.4rem 1rem",
          }}
        />
        {new Date(newOrders[0]?.createdAt).getTime() + 180 * 1000 >
          new Date().getTime() &&
          newOrders[0].orderType !== "outlet" && (
            <Button
              style={{
                marginLeft: "1rem",
                backgroundColor: "black",
                fontSize: "0.7rem",
                width: "15rem",
              }}
              className="marginPointEight numbersButton"
            >
              {newOrders?.map((ors, index) => (
                <div
                  key={index}
                  className="twinkle-text"
                  style={{ color: "orange" }}
                >
                  {ors.orderDetails?.map((item, idx) => (
                    <span key={idx}>
                      {item.itemName} - {item.itemQuantity}{" "}
                    </span>
                  ))}
                </div>
              ))}
            </Button>
          )}
      </Row>

      <br />

      <Row className="justify-content-center align-items-center">
        <Col xs={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col xs={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col xs={3}>
          <Button onClick={getOrderHandler}>Get Orders</Button>
        </Col>
      </Row>

      <br />

      <h6>
        {showDelivered
          ? "Delivered Orders"
          : showOutletOrders
          ? "Outlet Orders"
          : ""}
      </h6>

      <Row>
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message>{error?.data?.error}</Message>
        ) : orders ? (
          <div style={{ height: "70vh" }}>
            {searchText?.length > 0 && (
              <h6 style={{ color: "red" }}>You are seeing searched orders</h6>
            )}
            <div>
              <Table
                striped
                bordered
                hover
                responsive="sm"
                className="table-custom"
              >
                <thead className={`${userInfo.themeColor}ThemeThead`}>
                  <tr>
                    <th>Order Id</th>
                    <th>Names</th>
                    <th>Count</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Delivery OTP</th>
                    <th>Amount</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody className={`${userInfo.themeColor}ThemeTbody`}>
                  {filteredOrders?.map((ors) => (
                    <tr key={ors._id}>
                      <td>
                        <a
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            navigate("/orderDetails", {
                              state: { orderResponse: ors },
                            })
                          }
                        >
                          {ors.orderId}
                        </a>
                      </td>
                      <td>
                        {ors.orderDetails?.map((item, idx) => (
                          <div key={idx}>
                            {item.itemName} - {item.itemQuantity}
                          </div>
                        ))}
                      </td>
                      <td>
                        {ors.orderDetails.reduce(
                          (total, item) => total + Number(item.itemQuantity),
                          0
                        )}
                      </td>
                      <td>
                        {new Date(ors?.createdAt).toISOString().slice(0, 10)}
                      </td>
                      {/* <td>
                        <Form.Select
                          value={ors?.orderStatus}
                          onClick={() => {
                            setOrderId(ors?._id);
                            setDisplayOrderId(ors.orderId);
                            setNewStatus(ors?.orderStatus);
                            setDeliveryOtp("");
                            setShowModal(true);
                          }}
                        >
                          <option value="Placed">Placed</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="Preparing">Preparing</option>
                          <option value="Ready">Ready</option>
                          <option value="In_Delivery">In_Delivery</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Delivery_Rejected">
                            Delivery_Rejected
                          </option>
                          <option value="Could_Not_Deliver">
                            Could_Not_Deliver
                          </option>
                          <option value="Not_Placed">Not_Placed</option>
                          <option value="Rejected">Rejected</option>
                        </Form.Select>
                      </td> */}
                      <td>{ors.orderStatus}</td>
                      <td>{ors.deliveryOtp}</td>
                      <td>{ors.grossTotal}</td>
                      <td>
                        {new Date(
                          new Date(ors?.createdAt).getTime() +
                            5.5 * 60 * 60 * 1000
                        )
                          .toISOString()
                          .slice(11, 16)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <></>
        )}
      </Row>
    </>
  );
};

export default OrdersHistoryScreen;
